import React, { useState } from 'react';
import { tournamentApi } from '../../../services/api';

// ── Helpers ───────────────────────────────────────────────────────────────────

const toInitCap = (str) =>
  str ? str.split(/[\s_]+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';

const formatDate = (ds) =>
  ds ? new Date(ds).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

const formatDateTime = (ds) =>
  ds ? new Date(ds).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const toDateInput = (ds) => ds ? ds.split('T')[0] : '';

const toDateTimeInput = (ds) => {
  if (!ds) return '';
  const d = new Date(ds);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const ALL_SPORTS = [
  { value: 'badminton',    label: 'Badminton' },
  { value: 'table_tennis', label: 'Table Tennis' },
  { value: 'tennis',       label: 'Tennis' },
  { value: 'padel',        label: 'Padel' },
  { value: 'squash',       label: 'Squash' },
  { value: 'pickleball',   label: 'Pickleball' },
];

// ── Sub-components ────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  draft:        { bg: '#f3f4f6', color: '#6b7280',  label: 'Draft' },
  registration: { bg: '#dbeafe', color: '#1d4ed8',  label: 'Open for Registration' },
  seeding:      { bg: '#fef3c7', color: '#b45309',  label: 'Seeding' },
  in_progress:  { bg: '#d1fae5', color: '#065f46',  label: 'In Progress' },
  completed:    { bg: '#d1fae5', color: '#065f46',  label: 'Completed' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || { bg: '#f3f4f6', color: '#6b7280', label: toInitCap(status) };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
};

const SectionTitle = ({ children }) => (
  <h3 style={{ fontSize: '0.78rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 0.75rem' }}>
    {children}
  </h3>
);

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.55rem 0', borderBottom: '1px solid #f3f4f6' }}>
    <span style={{ fontSize: '0.82rem', color: '#6b7280', fontWeight: 500 }}>{label}</span>
    <span style={{ fontSize: '0.875rem', color: '#111827', fontWeight: 500, textAlign: 'right' }}>{value || '—'}</span>
  </div>
);

const FormSection = ({ title, children }) => (
  <div style={{ marginBottom: '1.5rem' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
      <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{title}</span>
      <div style={{ flex: 1, height: 1, background: '#f0f0f0' }} />
    </div>
    {children}
  </div>
);

const FG = ({ label, children }) => (
  <div className="form-group">
    <label className="form-label">{label}</label>
    {children}
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

const DetailsTab = ({ tournament, players, isOrganizer, isAuthenticated, isRegistered, onRegister, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const initForm = () => ({
    name:                  tournament.name || '',
    sport_type:            tournament.sport_type || 'badminton',
    draw_type:             tournament.draw_type || 'knockout',
    venue:                 tournament.venue || '',
    start_date:            toDateInput(tournament.start_date),
    end_date:              toDateInput(tournament.end_date),
    registration_deadline: toDateTimeInput(tournament.registration_deadline),
    status:                tournament.status || 'draft',
    entry_fee:             tournament.entry_fee ?? 0,
    prize_money:           tournament.prize_money || '',
    description:           tournament.description || '',
  });

  const [form, setForm] = useState(initForm);

  const handleEdit = () => {
    setForm(initForm());
    setSaveError('');
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setSaveError('');
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'number' ? (parseFloat(value) || 0) : value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setSaveError('Tournament name is required'); return; }
    if (!form.start_date)  { setSaveError('Start date is required'); return; }

    setSaving(true);
    setSaveError('');
    try {
      await tournamentApi.update(tournament.id, {
        name:                  form.name.trim(),
        sport_type:            form.sport_type,
        draw_type:             form.draw_type,
        venue:                 form.venue || null,
        start_date:            form.start_date,
        end_date:              form.end_date || null,
        registration_deadline: form.registration_deadline || null,
        status:                form.status,
        entry_fee:             parseFloat(form.entry_fee) || 0,
        prize_money:           form.prize_money || null,
        description:           form.description || null,
      });
      setEditing(false);
      onUpdate?.();
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // ── Edit form ──────────────────────────────────────────────────────────────

  if (editing) {
    return (
      <form onSubmit={handleSave}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700' }}>Edit Tournament Details</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" className="btn btn-outline" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>

        {saveError && (
          <div className="alert alert-error" style={{ marginBottom: '1.25rem' }}>{saveError}</div>
        )}

        <div className="card" style={{ padding: '1.5rem' }}>

          <FormSection title="Basic Info">
            <div className="grid grid-2">
              <FG label="Tournament Name *">
                <input type="text" name="name" className="form-input" value={form.name}
                  onChange={handleChange} required placeholder="Tournament name" />
              </FG>
              <FG label="Sport">
                <select name="sport_type" className="form-select" value={form.sport_type} onChange={handleChange}>
                  {ALL_SPORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </FG>
            </div>
            <div className="grid grid-2">
              <FG label="Format">
                <select name="draw_type" className="form-select" value={form.draw_type} onChange={handleChange}>
                  <option value="knockout">Knockout</option>
                  <option value="league">Round Robin / League</option>
                  <option value="group_knockout">Group + Knockout</option>
                </select>
              </FG>
              <FG label="Status">
                <select name="status" className="form-select" value={form.status} onChange={handleChange}>
                  <option value="draft">Draft</option>
                  <option value="registration">Open for Registration</option>
                  <option value="seeding">Seeding</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </FG>
            </div>
            <FG label="Venue">
              <input type="text" name="venue" className="form-input" value={form.venue}
                onChange={handleChange} placeholder="e.g., City Sports Complex" />
            </FG>
          </FormSection>

          <FormSection title="Schedule">
            <div className="grid grid-2">
              <FG label="Start Date *">
                <input type="date" name="start_date" className="form-input" value={form.start_date}
                  onChange={handleChange} required />
              </FG>
              <FG label="End Date">
                <input type="date" name="end_date" className="form-input" value={form.end_date}
                  onChange={handleChange} />
              </FG>
            </div>
            <FG label="Registration Deadline">
              <input type="datetime-local" name="registration_deadline" className="form-input"
                value={form.registration_deadline} onChange={handleChange} />
            </FG>
          </FormSection>

          <FormSection title="Capacity &amp; Financials">
            <FG label="Entry Fee (default per event)">
              <input type="number" name="entry_fee" className="form-input"
                value={form.entry_fee} onChange={handleChange} min="0" step="0.01" />
            </FG>
            <FG label="Prize Money">
              <input type="text" name="prize_money" className="form-input" value={form.prize_money}
                onChange={handleChange} placeholder="e.g., ₹10,000 winner prize" />
            </FG>
          </FormSection>

          <FormSection title="Description">
            <textarea name="description" className="form-textarea" rows={3}
              value={form.description} onChange={handleChange}
              placeholder="Tournament description, rules, notes…"
              style={{ resize: 'vertical' }} />
          </FormSection>

        </div>
      </form>
    );
  }

  // ── Read-only view ─────────────────────────────────────────────────────────

  return (
    <div>
      {isOrganizer && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <button className="btn btn-outline" onClick={handleEdit}>
            ✏️ Edit Tournament Details
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.25rem' }}>

        {/* Tournament Info */}
        <div className="card" style={{ padding: '1.5rem' }}>
          <SectionTitle>Tournament Info</SectionTitle>
          <InfoRow label="Sport"          value={toInitCap(tournament.sport_type)} />
          <InfoRow label="Format"         value={toInitCap(tournament.draw_type)} />
          <InfoRow label="Status"         value={<StatusBadge status={tournament.status} />} />
          <InfoRow label="Event Type"     value={tournament.event_type?.length > 0 ? tournament.event_type.map(toInitCap).join(', ') : null} />
          <InfoRow label="Event Category" value={tournament.event_category?.length > 0 ? tournament.event_category.map(toInitCap).join(', ') : null} />
          <InfoRow label="Age Category"   value={tournament.event_age_category?.length > 0 ? tournament.event_age_category.map(toInitCap).join(', ') : null} />
        </div>

        {/* Schedule & Logistics */}
        <div className="card" style={{ padding: '1.5rem' }}>
          <SectionTitle>Schedule &amp; Logistics</SectionTitle>
          <InfoRow label="Start Date"    value={formatDate(tournament.start_date)} />
          <InfoRow label="End Date"      value={formatDate(tournament.end_date)} />
          <InfoRow label="Reg. Deadline" value={tournament.registration_deadline ? formatDateTime(tournament.registration_deadline) : null} />
          <InfoRow label="Venue"         value={tournament.venue} />
          <InfoRow label="Participants"  value={players.length} />
          {tournament.entry_fee > 0 && <InfoRow label="Entry Fee"   value={`₹${tournament.entry_fee}`} />}
          {tournament.prize_money      && <InfoRow label="Prize Money" value={tournament.prize_money} />}
        </div>

      </div>

      {tournament.description && (
        <div className="card" style={{ padding: '1.5rem', marginTop: '1.25rem' }}>
          <SectionTitle>Description</SectionTitle>
          <p style={{ color: '#6b7280', lineHeight: 1.7, margin: 0, fontSize: '0.9rem' }}>{tournament.description}</p>
        </div>
      )}

      {isAuthenticated && !isOrganizer && tournament.status === 'registration' && !isRegistered && (
        <div style={{ marginTop: '1.25rem' }}>
          <button onClick={onRegister} className="btn btn-primary">Register for Tournament</button>
        </div>
      )}
      {isRegistered && (
        <div style={{ marginTop: '1.25rem' }}>
          <span className="badge badge-success">✓ You are registered</span>
        </div>
      )}
    </div>
  );
};

export default DetailsTab;
